import { FC, HTMLAttributes } from "react";

import Content, { ContentNS } from "../../../../content/content";
import ChecklistItem, {
  InfoItem,
} from "../../../../models/checklist/checklist-item/checklist-item";
import ChecklistItemStatus from "../../../../models/checklist/checklist-item/checklist-item-status";
import Body from "../../../shared/styles/typography/Body.styled";
import { Summary } from "../../../shared/styles/typography/Summary.styled";
import CardButton from "./components/card-button/CardButton";
import CardError from "./components/card-error/CardError";
import StatusLine from "./components/status-line/StatusLine";
import useCardErrorMessage from "./hooks/useCardErrorMessage";
import useMergedStatus from "./hooks/useMergedStatus";
import { capitalizeTitle } from "utils";
import CardContent from "./styles/CardContent.styled";
import CardFooter from "./styles/CardFooter.styled";
import StyledCard from "./styles/StyledCard.styled";
import { cardHasButton } from "./utils/cards-with-buttons";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import { getRedirectURLForItem } from "features/shared/hooks/navigation/useItemLevelRedirect";
import { useAppStore } from "store/store";
import usePaxSession from "features/shared/hooks/usePaxSession";

interface CardProps extends HTMLAttributes<HTMLElement> {
  highlight?: boolean;
  item: ChecklistItem;
}

const Card: FC<CardProps> = ({ item, highlight }) => {
  const textUI = (s: string) => Content.shared.t(s, { ns: ContentNS.ui });
  const textChecklist = (s: string, args: any = {}) =>
    Content.shared.t(s, { ns: ContentNS.checklist, ...args });

  const sessionId = useAppStore((state) => state.sessionId);
  const paxSession = usePaxSession();
  if (!paxSession) {
    throw new Error("invalid paxSession");
  }

  const navigate = useNavigateWithQuery();
  const status = useMergedStatus(item);

  const error = useCardErrorMessage(item);

  const buttonTitle =
    item instanceof InfoItem ? textUI("more-info") : textUI("how-to-resolve");

  const description = item.shortDescription
    ? item.shortDescription
    : textChecklist("item-subtitle", {
        itemTitle: item.shortTitle || item.title,
      });

  return (
    <StyledCard highlight={highlight} status={item.status}>
      <CardContent>
        <StatusLine status={status} isInfo={item instanceof InfoItem} />
        <Summary>
          {capitalizeTitle(
            `${textChecklist("item-title", {
              itemTitle: item.title,
            })}`
          )}
        </Summary>

        {item.status !== ChecklistItemStatus.Resolved ? (
          <Body>{`${description}`}</Body>
        ) : null}
      </CardContent>

      <CardFooter>
        {error ? <CardError message={error} /> : null}

        {cardHasButton(item, status) ? (
          <CardButton
            highlight={highlight}
            onClick={() =>
              navigate({
                pathname: getRedirectURLForItem(
                  sessionId,
                  paxSession.paxSessionId,
                  item
                ),
                search: "forceDocumentType=", // clear forceDocumentType
              })
            }
          >
            {buttonTitle}
          </CardButton>
        ) : null}
      </CardFooter>
    </StyledCard>
  );
};

export default Card;
